'use client';

import { useEffect } from 'react';

/**
 * Hold the page still behind an open sheet or modal.
 *
 * One lock for every overlay in the package, counted, so a sheet opened over
 * another sheet doesn't unlock the page when it closes. It locks inside a
 * widget too: a sheet that covers the page owns the page's scroll for as long
 * as it is open, whichever DOM it renders into.
 *
 * - html and body both go `overflow: hidden`, html with `!important`, because
 *   a host page can scroll on html with its own rule (woodsidebible.org's
 *   Salient theme sets `overflow-y: scroll` there).
 * - body goes `position: fixed` at the current offset, which is what stops
 *   iOS Safari scrolling the page behind under a finger.
 * - body is padded by the scrollbar's width, so hiding the scrollbar doesn't
 *   shift the page sideways where scrollbars take up room.
 * - The offset is restored instantly on unlock, even on a page with
 *   `scroll-behavior: smooth`, so the page doesn't glide back into place.
 */
let locks = 0;
let saved: { y: number; props: [CSSStyleDeclaration, string, string, string][] } | null = null;

/** Set a property, remembering only what it was, so other inline styles survive unlock */
function set(style: CSSStyleDeclaration, prop: string, value: string, important = false) {
  saved!.props.push([style, prop, style.getPropertyValue(prop), style.getPropertyPriority(prop)]);
  style.setProperty(prop, value, important ? 'important' : '');
}

function lock() {
  locks += 1;
  if (locks > 1) return;
  const html = document.documentElement;
  const body = document.body.style;
  const y = window.scrollY;
  const gap = window.innerWidth - html.clientWidth;
  saved = { y, props: [] };
  set(html.style, 'overflow', 'hidden', true);
  set(body, 'overflow', 'hidden');
  set(body, 'position', 'fixed');
  set(body, 'top', `-${y}px`);
  set(body, 'left', '0');
  set(body, 'right', '0');
  if (gap > 0) set(body, 'padding-right', `${gap}px`);
}

function unlock() {
  if (locks === 0) return;
  locks -= 1;
  if (locks > 0 || !saved) return;
  const { y, props } = saved;
  saved = null;
  for (const [style, prop, value, priority] of props.reverse()) {
    if (value) style.setProperty(prop, value, priority);
    else style.removeProperty(prop);
  }
  const html = document.documentElement.style;
  const behavior = html.getPropertyValue('scroll-behavior');
  html.setProperty('scroll-behavior', 'auto');
  window.scrollTo(0, y);
  if (behavior) html.setProperty('scroll-behavior', behavior);
  else html.removeProperty('scroll-behavior');
}

export function useScrollLock(active: boolean) {
  useEffect(() => {
    if (!active) return;
    lock();
    return unlock;
  }, [active]);
}
